import { useState } from 'react';

import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';

import { FREEZE_METHODS } from '@/constants';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  MassInterimEmbryosInput,
  StageGradeCountInput,
  TOA_GetFertilizationsForFreezing,
} from '@/rtk-query.gql';

import { useAddMassInterimEmbryos } from '../../../services/freeze/hooks';
import { useFreezeContext } from '../hooks/useFreezeContext';
import { SLAUGHTER_HOUSE_SITES } from '../utils/constants';
import { defaultGradingRows } from '../utils/constants';
import { FreezeGradingRow } from '../utils/types';
import { MassEmbryoCreationDialog } from './MassEmbryoCreationDialog';

import './AddFreezeEmbryoControls.css';

type AddFreezeEmbryoControlsProps = {
  onAdd: (quantity: number, stageGradeCounts?: StageGradeCountInput[]) => void;
  onDeactivate: () => void;
  refetch: () => void;
  rowData?: TOA_GetFertilizationsForFreezing;
};

export const AddFreezeEmbryoControls = ({
  onAdd,
  onDeactivate,
  refetch,
  rowData,
}: AddFreezeEmbryoControlsProps) => {
  const fixEmptyCaneCodes = useFeatureFlag('fix_empty_cane_codes');
  const {
    form,
    gradingGridData,
    setGradingGridKey,
    gradingGridRef,
    setGradingGridData,
  } = useFreezeContext();
  const [quantity, setQuantity] = useState<number>(1);
  const [showCreateEmbryoDialog, setShowCreateEmbryoDialog] =
    useState<boolean>();
  const [grade1, setGrade1] = useState(0);
  const [grade2, setGrade2] = useState(0);

  const isSlaughterHouse = SLAUGHTER_HOUSE_SITES.includes(
    rowData?.opuSite as string,
  );

  const { mutateAsync: addMassIntEmbryos } = useAddMassInterimEmbryos();

  const handleCreateEmbryos = () => {
    const tempEmbryoInput: MassInterimEmbryosInput = {
      fertID: rowData?.fertId as string,
      cane: fixEmptyCaneCodes ? rowData?.cane : '',
      g1Count: grade1,
      g2Count: grade2,
      freezeDay: form?.freezeDay?.toString() as string,
      storageLoc: form?.storageLocation as string,
      graderID: form?.grader as string,
      freezeTechID: form?.freezeTech as string,
      freezer: form?.freezer as string,
    };
    addMassIntEmbryos({
      input: tempEmbryoInput,
      dtplus:
        rowData?.freezeMethod?.includes(FREEZE_METHODS.VITRIFICATION) ||
        rowData?.freezeMethod?.includes(FREEZE_METHODS.GLYCEROL)
          ? false
          : (form?.dt as boolean),
    });
  };

  const handleAdd = () => {
    onAdd(quantity);
  };

  const handleOops = () => {
    onDeactivate();
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleAddGradingEmbryos = () => {
    const stageGradeCounts: StageGradeCountInput[] = [];
    gradingGridData?.forEach((row: FreezeGradingRow) => {
      for (let stage = 4; stage <= 9; stage++) {
        const value = row[`s${stage}` as keyof FreezeGradingRow];
        if (value && Number(value) > 0) {
          stageGradeCounts.push({
            stage,
            grade: row.grade,
            count: Number(value),
          });
        }
      }
    });
    onAdd(1, stageGradeCounts);

    if (gradingGridRef.current?.api) {
      gradingGridRef.current.api.forEachNode((node) => {
        for (let stage = 4; stage <= 9; stage++) {
          node.setDataValue(`s${stage}`, null);
        }
        node.setDataValue('total', 0);
      });

      gradingGridRef.current.api.refreshCells({ force: true });
    }

    setGradingGridData(defaultGradingRows);

    setGradingGridKey((prev) => prev + 1);
  };

  return (
    <div>
      <div className={`add-freeze-embryo-controls`}>
        {!isSlaughterHouse && (
          <>
            <div>Number To Add</div>
            <InputNumber
              value={quantity}
              onValueChange={(e) => setQuantity(e.value ?? 1)}
              showButtons
              min={1}
              size={3}
            />
          </>
        )}
        <MassEmbryoCreationDialog
          visible={showCreateEmbryoDialog as boolean}
          onHide={() => setShowCreateEmbryoDialog(false)}
          grade1={grade1}
          grade2={grade2}
          onGrade1Change={setGrade1}
          onGrade2Change={setGrade2}
          onAccept={handleCreateEmbryos}
        />
        <div className="add-freeze-embryo-controls__buttons">
          <Button
            icon="pi pi-plus"
            onClick={() =>
              isSlaughterHouse ? setShowCreateEmbryoDialog(true) : handleAdd()
            }
            severity="success"
            tooltip="Add Embryos"
          />
          <Button
            icon="pi pi-trash"
            onClick={handleOops}
            severity="danger"
            tooltip="Remove Selected Embryos"
          />
          <Button
            icon="pi pi-refresh"
            onClick={handleRefresh}
            severity="info"
            tooltip="Refresh Data"
          />
        </div>

        <Button
          onClick={handleAddGradingEmbryos}
          severity="success"
          label={'Add From\nGrading Grid'}
          disabled={!gradingGridData?.length}
          className="whitespace-pre-line"
        />
      </div>
    </div>
  );
};
